/**
 * The rule builder's DRAFT model — what a person is halfway through typing,
 * and the one door from it to the strict `RuleTree` the API accepts.
 *
 * ## WHY THERE ARE TWO SHAPES
 *
 * `@wend/shared`'s `ruleTreeSchema` is deliberately unforgiving: a `between`
 * with one number, an `any_of` with nothing ticked, a group with no children —
 * none of them are representable, so a malformed rule can never reach the
 * compiler. A builder cannot work in that shape. Somebody who clears a number
 * box to type a new one has, for a moment, a rule with no value; somebody who
 * just pressed "Add group" has a group whose rule they have not finished. So
 * the builder edits {@link DraftNode}s — the same tree, with `value: unknown` —
 * and {@link toRuleTree} is the only way out: it keeps the complete leaves,
 * drops the rest (and any group left empty), and answers `null` when nothing
 * complete remains. The preview is asked about exactly what would be saved.
 *
 * Saving does NOT silently drop a half-finished rule. {@link incompleteRuleIds}
 * is how the builder refuses to save while one is on screen — "the rule you
 * can see is not the rule that was saved" is the defect a draft model invites,
 * and the Save button is where it is closed.
 *
 * ## WHY THE COMPLETENESS TEST IS THE CONTRACT ITSELF
 *
 * A leaf is complete when `ruleLeafSchema` parses it. Not "when the value box
 * is non-empty": a reversed `between`, a duplicate in `any_of` or a date that
 * does not exist are all non-empty and all refused by the API. One test, owned
 * by the contract, so the builder and the server cannot disagree about which
 * rules count.
 *
 * ## WHAT IS NOT HERE
 *
 * No list of field names. The fields come from the workspace's catalogue
 * (`GET /fields`) — every uploaded column, whatever it is called — and
 * {@link nextRuleField} picks a starting one from whatever that catalogue holds.
 * The concept's `ADD_ORDER` named its sample columns; a real workspace's columns
 * are the owner's brief: "it should not be fixed".
 *
 * Ids are minted from `crypto.randomUUID` rather than a module counter. A
 * counter at module scope restarts at 1 on every page load, and an edited
 * segment loads ids minted by an earlier load — `r1` twice in one tree is a
 * tree the API refuses (`ruleTreeSchema` requires distinct ids).
 */
import {
  OPERATORS_BY_TYPE,
  RULE_MAX_CHILDREN,
  RULE_MAX_DEPTH,
  RULE_MAX_LEAVES,
  operatorNeedsValue,
  ruleLeafSchema,
  ruleTreeIssues,
  ruleTreeSchema,
  type Field,
  type FieldCatalogueResponse,
  type FieldType,
  type RuleFieldInfo,
  type RuleGroup,
  type RuleIssue,
  type RuleJoin,
  type RuleLeaf,
  type RuleNode,
  type RuleOperator,
  type RuleTree,
  type SegmentPreviewResponse,
} from '@wend/shared';

/* -------------------------------------------------------------------------- */
/* Draft shapes                                                                */
/* -------------------------------------------------------------------------- */

export interface DraftRule {
  readonly kind: 'rule';
  readonly id: string;
  readonly field: string;
  readonly op: RuleOperator;
  /** Whatever the editor holds right now — possibly incomplete. */
  readonly value: unknown;
}

export interface DraftGroup {
  readonly kind: 'group';
  readonly id: string;
  readonly join: RuleJoin;
  readonly children: readonly DraftNode[];
}

export type DraftNode = DraftRule | DraftGroup;

/** A fresh id the rule-tree contract accepts (`[A-Za-z0-9_-]{1,64}`). */
export function draftId(prefix: 'r' | 'g'): string {
  const random =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID().replace(/-/g, '').slice(0, 12)
      : Math.random().toString(36).slice(2, 14);
  return `${prefix}_${random}`;
}

/* -------------------------------------------------------------------------- */
/* The catalogue, indexed                                                      */
/* -------------------------------------------------------------------------- */

/** The catalogue as the builder reads it: every field, by key, and the denominator. */
export interface FieldIndex {
  readonly fields: readonly Field[];
  readonly byKey: ReadonlyMap<string, Field>;
  /** People in the workspace — the denominator every coverage is a share of. */
  readonly people: number;
}

export function indexCatalogue(catalogue: Pick<FieldCatalogueResponse, 'fields' | 'people'>): FieldIndex {
  return {
    fields: catalogue.fields,
    byKey: new Map(catalogue.fields.map((field) => [field.key, field])),
    people: catalogue.people,
  };
}

/**
 * The resolver `ruleTreeIssues` takes. A hidden field resolves to nothing — the
 * contract's own rule — so a rule over a column somebody hid since is reported
 * as unknown, by name, rather than compiled against a field the person removed
 * from view.
 */
export function fieldResolver(index: FieldIndex): (key: string) => RuleFieldInfo | null {
  return (key) => {
    const field = index.byKey.get(key);
    if (field === undefined || field.hidden) return null;
    return { type: field.type, label: field.label, needsDateFormat: field.needsDateFormat };
  };
}

/** Fields a rule may be written over: not hidden. A date still waiting for its format is offered, with the question. */
export function pickableFields(index: FieldIndex): Field[] {
  return index.fields.filter((field) => !field.hidden);
}

/* -------------------------------------------------------------------------- */
/* Values                                                                      */
/* -------------------------------------------------------------------------- */

/** Today in IST, `YYYY-MM-DD` — the zone every date rule is evaluated in. */
export function todayIst(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

function daysBefore(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d - days));
  return date.toISOString().slice(0, 10);
}

const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

/**
 * A starting value for an operator, carrying over what the previous operator
 * held where it still makes sense — "over 25" switched to "between" starts at
 * 25–35, not at nothing. Numbers with no previous value start EMPTY rather than
 * at a made-up figure: the concept's suggested values were tuned to its sample
 * columns, and "Loyalty points ≥ 25" invented for a real column would be a rule
 * nobody wrote.
 */
export function defaultValue(type: FieldType, op: RuleOperator, previous: unknown, field?: Pick<Field, 'values'>): unknown {
  if (!operatorNeedsValue(op)) return null;
  const options = field?.values ?? null;
  switch (op) {
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte':
    case 'eq':
      if (finite(previous)) return previous;
      if (Array.isArray(previous) && finite(previous[0])) return previous[0];
      return null;
    case 'between': {
      if (type === 'date') {
        const today = todayIst();
        if (typeof previous === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(previous)) return [previous, today];
        return [daysBefore(today, 30), today];
      }
      const base = finite(previous) ? previous : Array.isArray(previous) && finite(previous[0]) ? previous[0] : null;
      return base === null ? [null, null] : [base, base + 10];
    }
    case 'in_last':
    case 'not_in_last':
      if (previous !== null && typeof previous === 'object' && !Array.isArray(previous) && 'amount' in previous) return previous;
      return { amount: 30, unit: 'days' };
    case 'before':
    case 'after':
      if (typeof previous === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(previous)) return previous;
      if (Array.isArray(previous) && typeof previous[0] === 'string') return previous[0];
      return daysBefore(todayIst(), 30);
    case 'anniversary_in_next':
      return { days: 7 };
    case 'any_of':
      if (Array.isArray(previous)) return previous.filter((value) => typeof value === 'string');
      if (typeof previous === 'string' && previous.trim() !== '') return [previous];
      return options !== null && options.length > 0 ? [options[0]] : [];
    case 'is':
    case 'is_not':
      if (typeof previous === 'string' && previous !== '') return previous;
      if (Array.isArray(previous) && typeof previous[0] === 'string') return previous[0];
      return options !== null && options.length > 0 ? options[0] : '';
    case 'contains':
      return typeof previous === 'string' ? previous : '';
    default:
      return null;
  }
}

/** A new rule over `field`, on its type's first operator. */
export function makeRule(field: Pick<Field, 'key' | 'type' | 'values'>): DraftRule {
  const op = OPERATORS_BY_TYPE[field.type][0] as RuleOperator;
  return { kind: 'rule', id: draftId('r'), field: field.key, op, value: defaultValue(field.type, op, null, field) };
}

export function makeGroup(join: RuleJoin, children: readonly DraftNode[]): DraftGroup {
  return { kind: 'group', id: draftId('g'), join, children };
}

/** A rule switched to another field: that field's first operator, a fresh value. */
export function retargetRule(rule: DraftRule, field: Pick<Field, 'key' | 'type' | 'values'>): DraftRule {
  const op = OPERATORS_BY_TYPE[field.type][0] as RuleOperator;
  return { ...rule, field: field.key, op, value: defaultValue(field.type, op, null, field) };
}

/** A rule switched to another operator, keeping what still fits. */
export function reoperateRule(rule: DraftRule, op: RuleOperator, field: Pick<Field, 'type' | 'values'> | undefined): DraftRule {
  return { ...rule, op, value: defaultValue(field?.type ?? 'text', op, rule.value, field) };
}

/* -------------------------------------------------------------------------- */
/* Tree edits                                                                  */
/* -------------------------------------------------------------------------- */

export function updateNode(node: DraftGroup, id: string, change: (node: DraftNode) => DraftNode): DraftGroup;
export function updateNode(node: DraftNode, id: string, change: (node: DraftNode) => DraftNode): DraftNode;
export function updateNode(node: DraftNode, id: string, change: (node: DraftNode) => DraftNode): DraftNode {
  if (node.id === id) return change(node);
  if (node.kind !== 'group') return node;
  return { ...node, children: node.children.map((child) => updateNode(child, id, change)) };
}

export function removeNode(node: DraftGroup, id: string): DraftGroup {
  return {
    ...node,
    children: node.children
      .filter((child) => child.id !== id)
      .map((child) => (child.kind === 'group' ? removeNode(child, id) : child)),
  };
}

/** Every leaf, depth-first. */
export function draftLeaves(node: DraftNode): DraftRule[] {
  if (node.kind === 'rule') return [node];
  return node.children.flatMap(draftLeaves);
}

/** Every group, root first. */
export function draftGroups(node: DraftNode): DraftGroup[] {
  if (node.kind === 'rule') return [];
  return [node, ...node.children.flatMap(draftGroups)];
}

/** The field to start a new rule on: the first usable one the tree does not read yet, columns first. */
export function nextRuleField(index: FieldIndex, tree: DraftNode | null): Field | null {
  const used = new Set(tree === null ? [] : draftLeaves(tree).map((leaf) => leaf.field));
  const usable = index.fields.filter((field) => !field.hidden && field.usableInRules);
  const order = ['columns', 'derived', 'profile', 'activity'] as const;
  for (const group of order) {
    const found = usable.find((field) => field.group === group && !used.has(field.key));
    if (found !== undefined) return found;
  }
  return usable[0] ?? null;
}

/** Room for another child here — the contract's limits, checked before a button offers one. */
export function canAddChild(group: DraftGroup, root: DraftGroup): boolean {
  return group.children.length < RULE_MAX_CHILDREN && draftLeaves(root).length < RULE_MAX_LEAVES;
}

/** Depth of `group` counted the contract's way: the root is 1. Another group fits inside while this is below the maximum. */
export function canNestInside(depth: number): boolean {
  return depth + 1 < RULE_MAX_DEPTH;
}

/* -------------------------------------------------------------------------- */
/* Draft → contract                                                            */
/* -------------------------------------------------------------------------- */

/** The leaf as the contract parses it, or `null` while it is incomplete. */
export function toLeaf(rule: DraftRule): RuleLeaf | null {
  const candidate = operatorNeedsValue(rule.op)
    ? { kind: 'rule', id: rule.id, field: rule.field, op: rule.op, value: rule.value }
    : { kind: 'rule', id: rule.id, field: rule.field, op: rule.op };
  const parsed = ruleLeafSchema.safeParse(candidate);
  return parsed.success ? parsed.data : null;
}

function toGroup(group: DraftGroup): RuleGroup | null {
  const children: RuleNode[] = [];
  for (const child of group.children) {
    const converted = child.kind === 'group' ? toGroup(child) : toLeaf(child);
    if (converted !== null) children.push(converted);
  }
  return children.length === 0 ? null : { kind: 'group', id: group.id, join: group.join, children };
}

/**
 * The complete part of a draft, as the API takes it — or `null` when nothing
 * complete is left. Checked against `ruleTreeSchema` as a whole, so a tree the
 * API would refuse (too deep, too many rules) is never sent either.
 */
export function toRuleTree(draft: DraftGroup): RuleTree | null {
  const tree = toGroup(draft);
  if (tree === null) return null;
  return ruleTreeSchema.safeParse(tree).success ? tree : null;
}

/** Leaves still missing a valid value — the builder will not save while there are any. */
export function incompleteRuleIds(draft: DraftGroup): Set<string> {
  return new Set(draftLeaves(draft).filter((leaf) => toLeaf(leaf) === null).map((leaf) => leaf.id));
}

/** A saved tree, opened for editing. The shapes are the same; only the value's type loosens. */
export function fromRuleTree(tree: RuleTree): DraftGroup {
  const convert = (node: RuleNode): DraftNode =>
    node.kind === 'group'
      ? { kind: 'group', id: node.id, join: node.join, children: node.children.map(convert) }
      : { kind: 'rule', id: node.id, field: node.field, op: node.op, value: 'value' in node ? (node.value ?? null) : null };
  return convert(tree) as DraftGroup;
}

/** A new, empty root: "People who match all of…" with nothing in it yet. */
export function emptyRoot(): DraftGroup {
  return { kind: 'group', id: 'root', join: 'and', children: [] };
}

/* -------------------------------------------------------------------------- */
/* Issues and counts                                                           */
/* -------------------------------------------------------------------------- */

/** The catalogue's objections to the complete part of the tree, by rule id. */
export function issuesById(tree: RuleTree | null, index: FieldIndex): Map<string, RuleIssue> {
  const out = new Map<string, RuleIssue>();
  if (tree === null) return out;
  for (const issue of ruleTreeIssues(tree, fieldResolver(index))) {
    if (!out.has(issue.ruleId)) out.set(issue.ruleId, issue);
  }
  return out;
}

/** The preview's per-rule and per-group counts, by id. */
export function countsById(preview: SegmentPreviewResponse | null): Map<string, number> {
  const out = new Map<string, number>();
  if (preview === null) return out;
  for (const entry of preview.perRule) out.set(entry.id, entry.count);
  for (const entry of preview.perGroup) out.set(entry.id, entry.count);
  return out;
}

/**
 * Where the live counts stand. `idle` is "nothing complete to count yet" — a
 * different fact from a count of zero, and drawn differently.
 */
export type PreviewState =
  | { readonly status: 'idle' }
  | { readonly status: 'pending'; readonly last: SegmentPreviewResponse | null }
  | { readonly status: 'ready'; readonly data: SegmentPreviewResponse }
  | { readonly status: 'error'; readonly message: string; readonly last: SegmentPreviewResponse | null };

export function previewData(state: PreviewState): SegmentPreviewResponse | null {
  switch (state.status) {
    case 'ready':
      return state.data;
    case 'pending':
    case 'error':
      return state.last;
    default:
      return null;
  }
}
